import React, { useEffect, useState } from "react";
import SettingToggle from "./SettingToggle";

const API     = `${process.env.REACT_APP_BACKEND_URL}/api/omega/v2`;
const USER_ID = "default";

// Frontend/backend are different origins in production, so document.cookie
// can't see the csrf_token cookie the backend sets — login/auth-me responses
// hand it back in the JSON body instead, stashed in localStorage under this
// same key by App.js. Fall back to the cookie for same-origin setups.
function csrfHeaders() {
  try {
    const stored = localStorage.getItem("roi_csrf_token");
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
}

// ── Autopilot autonomy levels ──────────────────────────────────────────────
// Same 4 levels as the status bar (App.js) and Mobile Settings
// (AutopilotQuickPanel in MobileSettingsExpanded.js) — kept in sync by hand
// since each surface fetches/writes independently rather than sharing state.
const AUTONOMY_LEVELS = [
  { id: "basic", label: "Basic", minZi: 85, color: "#8e8e93", desc: "Zi ≥ 85 · Single-leg only · Conservative" },
  { id: "pro",   label: "Pro",   minZi: 78, color: "#30d158", desc: "Zi ≥ 78 · Single-leg + verticals · Balanced" },
  { id: "ultra", label: "Ultra", minZi: 72, color: "#60a5fa", desc: "Zi ≥ 72 · All strategies · Aggressive" },
  { id: "omega", label: "Omega", minZi: 68, color: "#a78bfa", desc: "Zi ≥ 68 · All strategies · Full Kelly" },
];

// ── Personality ────────────────────────────────────────────────────────────
const PERSONALITIES = ["BEST_FRIEND", "FRIENDLY_TEXT", "OPTIMISTIC", "STRAIGHTFORWARD", "HYPE_TRADER", "CALM_COACH", "RISK_MANAGER", "COMPLEX"];
const PERSONALITY_DESC = {
  BEST_FRIEND:     "Texting your trading buddy — short, casual, real",
  FRIENDLY_TEXT:   "Warm, supportive, conversational",
  OPTIMISTIC:      "Upbeat, opportunity-focused",
  STRAIGHTFORWARD: "Blunt, no filler",
  HYPE_TRADER:     "High energy, meme-aware",
  CALM_COACH:      "Patient, educational",
  RISK_MANAGER:    "Cautious, risk-first framing",
  COMPLEX:         "Full briefing — tables, charts, scenarios, all the data",
};

// ── Response depth ─────────────────────────────────────────────────────────
const DEPTHS = ["QUICK", "NORMAL", "EXPLAIN", "DEEP"];
const DEPTH_DESC = {
  QUICK:   "One sentence verdict",
  NORMAL:  "Short paragraph",
  EXPLAIN: "Full reasoning included",
  DEEP:    "All context + alternatives",
};

// ── Text voice style ───────────────────────────────────────────────────────
const VOICES = ["NATURAL", "WARM", "DIRECT", "CALM", "ENERGETIC"];
const VOICE_DESC = {
  NATURAL:  "Balanced, readable",
  WARM:     "Friendly and encouraging",
  DIRECT:   "Sharp, minimal words",
  CALM:     "Measured, reassuring",
  ENERGETIC:"Fast, punchy",
};

// ── Risk appetite ──────────────────────────────────────────────────────────
const RISK_APPETITES = [
  { id: "CONSERVATIVE", label: "Conservative", desc: "Protect capital first. Small size, high conviction only.", color: "#38bdf8" },
  { id: "BALANCED",     label: "Balanced",     desc: "Standard risk/reward. Follow Omega's default rules.",    color: "#a78bfa" },
  { id: "AGGRESSIVE",   label: "Aggressive",   desc: "Bigger size, higher risk tolerance, faster triggers.",   color: "#fbbf24" },
  { id: "DEGEN",        label: "Degen",        desc: "Max size, YOLO energy. You know the risk.",             color: "#f87171" },
];

// ── TTS audio voices ───────────────────────────────────────────────────────
const TTS_VOICES = [
  { id: "nova",    label: "Nova",    desc: "Bright, warm, friendly female" },
  { id: "alloy",   label: "Alloy",   desc: "Neutral, versatile, professional" },
  { id: "echo",    label: "Echo",    desc: "Deep, smooth male" },
  { id: "fable",   label: "Fable",   desc: "Expressive, British-accented" },
  { id: "onyx",    label: "Onyx",    desc: "Rich, authoritative male" },
  { id: "shimmer", label: "Shimmer", desc: "Clear, energetic female" },
];

// ── Avatar presets ─────────────────────────────────────────────────────────
const AVATAR_COLORS = ["#38bdf8","#a78bfa","#4ade80","#fbbf24","#f87171","#fb923c","#e879f9","#2dd4bf"];
const AVATAR_EMOJIS = ["⚡","🧠","🎯","💎","🦅","🔥","🌊","🤖","👁️","🏆","💼","🎲"];

const DEFAULT_SETTINGS = {
  personality: "FRIENDLY_TEXT",
  depth: "NORMAL",
  voice: "NATURAL",
  tts_voice: "nova",
  risk_appetite: "BALANCED",
  assistant_name: "Omega",
  assistant_greeting: "Hey — I'm Omega, your AI trading assistant. I have live access to your portfolio, market prices, options chains, Greeks, news, flow, and every Omega engine. Ask me anything.",
  avatar_color: "#a78bfa",
  avatar_emoji: "⚡",
};

function saveLocally(updated) {
  try {
    localStorage.setItem("zi_ai_persona", JSON.stringify({
      name: updated.assistant_name,
      greeting: updated.assistant_greeting,
      avatar_color: updated.avatar_color,
      avatar_emoji: updated.avatar_emoji,
      risk_appetite: updated.risk_appetite,
      tts_voice: updated.tts_voice,
      personality: updated.personality,
      depth: updated.depth,
      voice: updated.voice,
    }));
    if (updated.tts_voice) localStorage.setItem("zi_tts_voice", updated.tts_voice);
  } catch {}
}

export default function OmegaSettingsPanel() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saved, setSaved]       = useState(false);
  const [testing, setTesting]   = useState(false);
  const [section, setSection]   = useState("identity");
  const [diag, setDiag]         = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [breakerResetting, setBreakerResetting] = useState(false);
  const [showOmegaBar, setShowOmegaBar] = useState(() => localStorage.getItem("zi_showOmegaBar") !== "false");
  const [autonomyLevel, setAutonomyLevel] = useState(null);
  const [autonomyLoading, setAutonomyLoading] = useState(false);
  const [autonomySaving, setAutonomySaving] = useState(false);
  const [autonomyFlash, setAutonomyFlash] = useState(false);

  useEffect(() => {
    fetch(`${API}/settings/${USER_ID}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (d?.personality || d?.assistant_name) setSettings(s => ({ ...DEFAULT_SETTINGS, ...s, ...d })); })
      .catch(() => {});
  }, []);

  const update = (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    setSaved(false);
    saveLocally(updated);
    fetch(`${API}/settings/${USER_ID}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      body: JSON.stringify(updated),
    }).then(() => { setSaved(true); setTimeout(() => setSaved(false), 2500); }).catch(() => {});
  };

  const testVoice = async (voiceId) => {
    if (testing) return;
    setTesting(true);
    try {
      const name = settings.assistant_name || "Omega";
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zi/tts/speak`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({
          text: `Hi, I'm ${name}. ${settings.assistant_greeting || "I'm your AI trading assistant."}`,
          voice: voiceId,
        }),
      });
      if (resp.ok) {
        const blob  = await resp.blob();
        const url   = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => { URL.revokeObjectURL(url); setTesting(false); };
        audio.onerror = () => { URL.revokeObjectURL(url); setTesting(false); };
        audio.play();
      } else { setTesting(false); }
    } catch { setTesting(false); }
  };

  const SECTIONS = [
    { id: "identity",    label: "Identity" },
    { id: "personality", label: "Personality" },
    { id: "voice",       label: "Voice & Audio" },
    { id: "risk",        label: "Risk Style" },
    { id: "autopilot",   label: "Autopilot" },
    { id: "floor",       label: "Floor Chat" },
    { id: "diagnostics", label: "Diagnostics" },
  ];

  const fetchDiag = async () => {
    setDiagLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/_internal/llm-stats`, {
        credentials: "include",
      });
      if (res.ok) setDiag(await res.json());
      else if (res.status === 403) setDiag({ _error: "Admin role required to view LLM stats." });
    } catch {}
    setDiagLoading(false);
  };

  const resetBreaker = async () => {
    setBreakerResetting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/_internal/llm-reset-breaker`, {
        method: "POST",
        credentials: "include",
        headers: csrfHeaders(),
      });
      if (res.ok) { await fetchDiag(); }
    } catch {}
    setBreakerResetting(false);
  };

  const toggleOmegaBar = (next) => {
    setShowOmegaBar(next);
    try {
      localStorage.setItem("zi_showOmegaBar", String(next));
      window.dispatchEvent(new CustomEvent("zi-omega-bar-visibility-changed", { detail: { visible: next } }));
    } catch {}
  };

  const fetchAutonomy = async () => {
    setAutonomyLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/omega/status`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setAutonomyLevel((d.autonomyLevel || "pro").toLowerCase());
      }
    } catch {}
    setAutonomyLoading(false);
  };

  const changeAutonomyLevel = async (level) => {
    if (autonomySaving || level === autonomyLevel) return;
    const prev = autonomyLevel;
    setAutonomyLevel(level);
    setAutonomySaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/omega/autonomy`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ level }),
      });
      if (!res.ok) throw new Error("save failed");
      setAutonomyFlash(true);
      setTimeout(() => setAutonomyFlash(false), 1400);
    } catch {
      setAutonomyLevel(prev);
    }
    setAutonomySaving(false);
  };

  const pill = (id, label) => (
    <button key={id} onClick={() => { setSection(id); if (id === "diagnostics") fetchDiag(); if (id === "autopilot" && autonomyLevel === null) fetchAutonomy(); }} style={{
      padding: "6px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
      border: section === id ? "1px solid #a78bfa" : "1px solid var(--border)",
      background: section === id ? "rgba(167,139,250,0.15)" : "var(--layer2)",
      color: section === id ? "#a78bfa" : "var(--muted)",
    }}>{label}</button>
  );

  return (
    <div className="card" style={{ maxWidth: 560 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: settings.avatar_color || "#a78bfa", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
          {settings.avatar_emoji || "⚡"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text)" }}>{settings.assistant_name || "Omega"}</div>
          <div style={{ fontSize: 10, color: "var(--muted)" }}>Your AI trading assistant</div>
        </div>
        {saved && <span style={{ fontSize: 10, color: "var(--green)", background: "rgba(74,222,128,0.1)", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>Saved</span>}
      </div>

      {/* Section tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
        {SECTIONS.map(s => pill(s.id, s.label))}
      </div>

      {/* ── IDENTITY ─────────────────────────────────────────────────────── */}
      {section === "identity" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Name */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.5 }}>Assistant Name</div>
            <input
              value={settings.assistant_name || ""}
              onChange={e => update("assistant_name", e.target.value.slice(0, 24))}
              placeholder="Omega"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 14, fontWeight: 600, outline: "none", boxSizing: "border-box" }}
            />
            <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 4 }}>This is what your assistant calls itself in conversation.</div>
          </div>

          {/* Greeting */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.5 }}>Opening Greeting</div>
            <textarea
              value={settings.assistant_greeting || ""}
              onChange={e => update("assistant_greeting", e.target.value.slice(0, 300))}
              rows={3}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 13, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
            />
            <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 4 }}>Shown when a new conversation starts.</div>
          </div>

          {/* Avatar color */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.5 }}>Avatar Color</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {AVATAR_COLORS.map(c => (
                <div key={c} onClick={() => update("avatar_color", c)} style={{
                  width: 32, height: 32, borderRadius: 8, background: c, cursor: "pointer",
                  boxShadow: settings.avatar_color === c ? `0 0 0 3px white, 0 0 0 5px ${c}` : "none",
                  transition: "box-shadow 0.15s",
                }} />
              ))}
            </div>
          </div>

          {/* Avatar emoji */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.5 }}>Avatar Icon</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {AVATAR_EMOJIS.map(e => (
                <button key={e} onClick={() => update("avatar_emoji", e)} style={{
                  width: 38, height: 38, borderRadius: 10, fontSize: 18, cursor: "pointer",
                  border: settings.avatar_emoji === e ? `2px solid ${settings.avatar_color || "#a78bfa"}` : "1px solid var(--border)",
                  background: settings.avatar_emoji === e ? `${settings.avatar_color || "#a78bfa"}22` : "var(--layer2)",
                }}>{e}</button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)", display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: settings.avatar_color || "#a78bfa", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>{settings.avatar_emoji || "⚡"}</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: settings.avatar_color || "#a78bfa", marginBottom: 3 }}>{settings.assistant_name || "Omega"}</div>
              <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.4 }}>{settings.assistant_greeting || "Hey — I'm your AI trading assistant."}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── PERSONALITY ──────────────────────────────────────────────────── */}
      {section === "personality" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {[
            { key: "personality", label: "Personality", options: PERSONALITIES, desc: PERSONALITY_DESC },
            { key: "depth",       label: "Response Depth", options: DEPTHS,    desc: DEPTH_DESC },
            { key: "voice",       label: "Text Voice Style", options: VOICES,  desc: VOICE_DESC },
          ].map(({ key, label, options, desc }) => (
            <div key={key}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {options.map(opt => (
                  <button key={opt} onClick={() => update(key, opt)} style={{
                    fontSize: 11, padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontWeight: 600,
                    border: settings[key] === opt ? "1px solid #a78bfa" : "1px solid var(--border)",
                    background: settings[key] === opt ? "rgba(167,139,250,0.15)" : "var(--layer2)",
                    color: settings[key] === opt ? "#a78bfa" : "var(--text)",
                  }}>{opt.replace(/_/g, " ")}</button>
                ))}
              </div>
              <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 5 }}>{desc[settings[key]]}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── VOICE & AUDIO ────────────────────────────────────────────────── */}
      {section === "voice" && (
        <div>
          <div style={{ fontSize: 11, color: "var(--dim)", marginBottom: 12, lineHeight: 1.5 }}>
            Picks the voice used whenever your assistant's replies are read aloud (text-to-speech). This only affects spoken audio — it doesn't change how it writes.
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
            TTS Audio Voice
            <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 500, color: "var(--dim)", textTransform: "none", letterSpacing: 0 }}>Press TEST to hear a sample in your greeting</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 8 }}>
            {TTS_VOICES.map(v => {
              const active = (settings.tts_voice || "nova") === v.id;
              return (
                <div key={v.id} onClick={() => update("tts_voice", v.id)} style={{
                  padding: "12px 14px", borderRadius: 14, cursor: "pointer",
                  border: active ? `1px solid ${settings.avatar_color || "#a78bfa"}` : "1px solid var(--border)",
                  background: active ? `${settings.avatar_color || "#a78bfa"}18` : "var(--layer2)",
                  display: "flex", flexDirection: "column", gap: 6,
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: active ? (settings.avatar_color || "#a78bfa") : "var(--text)" }}>{v.label}</span>
                    <button onClick={e => { e.stopPropagation(); testVoice(v.id); }} disabled={testing} style={{
                      background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.3)",
                      color: "#60a5fa", fontSize: 9, fontWeight: 700, padding: "3px 7px", borderRadius: 6, cursor: testing ? "default" : "pointer",
                    }}>{testing ? "…" : "▶ TEST"}</button>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--dim)" }}>{v.desc}</div>
                  {active && <div style={{ fontSize: 9, fontWeight: 700, color: settings.avatar_color || "#a78bfa" }}>● ACTIVE</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── RISK STYLE ───────────────────────────────────────────────────── */}
      {section === "risk" && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Risk Appetite</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {RISK_APPETITES.map(r => {
              const active = settings.risk_appetite === r.id;
              return (
                <div key={r.id} onClick={() => update("risk_appetite", r.id)} style={{
                  padding: "14px 16px", borderRadius: 14, cursor: "pointer",
                  border: active ? `1px solid ${r.color}` : "1px solid var(--border)",
                  background: active ? `${r.color}18` : "var(--layer2)",
                }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: active ? r.color : "var(--text)", marginBottom: 4 }}>{r.label}</div>
                  <div style={{ fontSize: 11, color: "var(--dim)", lineHeight: 1.35 }}>{r.desc}</div>
                  {active && <div style={{ fontSize: 9, fontWeight: 700, color: r.color, marginTop: 6 }}>● SELECTED</div>}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)", fontSize: 11, color: "var(--dim)" }}>
            Risk appetite shapes how {settings.assistant_name || "Omega"} frames trade suggestions, position sizing commentary, and how aggressively it flags opportunities.
          </div>
        </div>
      )}

      {/* ── AUTOPILOT ────────────────────────────────────────────────────── */}
      {section === "autopilot" && (
        <div>
          <div style={{ fontSize: 11, color: "var(--dim)", marginBottom: 16, lineHeight: 1.5 }}>
            Controls the live trading status bar pinned to the top of the screen (capital tier, autonomy level, Zi gate, risk %, position slots, drift/kill-switch status, and the HALT button). Turn it off to reclaim the space — the autonomy level below stays adjustable here either way.
          </div>

          <div style={{ padding: 16, borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)", marginBottom: 20 }}>
            <SettingToggle
              testId="omega-bar-visibility-toggle"
              label="Trading status bar"
              description={showOmegaBar ? "Shown at the top of the screen." : "Hidden. Autonomy level is still adjustable below."}
              checked={showOmegaBar}
              onChange={toggleOmegaBar}
              accent="#38bdf8"
            />
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Autonomy Level
          </div>
          <div style={{ fontSize: 11, color: "var(--dim)", marginBottom: 12, lineHeight: 1.5 }}>
            Sets the Zi confidence gate, risk % per trade, and position slots the autopilot trades within — a stricter level (Basic) only takes higher-conviction setups; a looser one (Omega) trades more of what it finds. This is the same control shown in the status bar above, and in Mobile Settings.
          </div>
          {autonomyLoading && !autonomyLevel && (
            <div style={{ fontSize: 12, color: "var(--dim)", textAlign: "center", padding: "16px 0" }}>Loading…</div>
          )}
          {autonomyLevel && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                {AUTONOMY_LEVELS.map(l => {
                  const active = autonomyLevel === l.id;
                  return (
                    <button
                      key={l.id}
                      data-testid={`omega-autonomy-${l.id}`}
                      onClick={() => changeAutonomyLevel(l.id)}
                      disabled={autonomySaving}
                      title={l.desc}
                      style={{
                        padding: "12px 4px", borderRadius: 12, cursor: autonomySaving ? "default" : "pointer",
                        textAlign: "center", border: `1.5px solid ${active ? l.color : "var(--border)"}`,
                        background: active ? `${l.color}18` : "var(--layer2)",
                        color: active ? l.color : "var(--muted)",
                        opacity: autonomySaving && !active ? 0.55 : 1, transition: "all 180ms",
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 800 }}>{autonomyFlash && active ? "✓" : l.label}</div>
                      <div style={{ fontSize: 9, opacity: 0.75, marginTop: 2 }}>Zi≥{l.minZi}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 8, lineHeight: 1.5 }}>
                {AUTONOMY_LEVELS.find(l => l.id === autonomyLevel)?.desc}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── DIAGNOSTICS ──────────────────────────────────────────────────── */}
      {section === "diagnostics" && (
        <div>
          <div style={{ fontSize: 11, color: "var(--dim)", marginBottom: 14, lineHeight: 1.5 }}>
            Admin-only live status of the LLM provider powering chat/voice replies — whether the circuit breaker has tripped (auto-pauses calls after repeated failures), recent latency, and call success/error counts. Not a user preference — nothing here is saved, it's a read-only snapshot.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, flex: 1 }}>LLM Health</div>
            <button onClick={fetchDiag} disabled={diagLoading} style={{
              padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer",
              border: "1px solid var(--border)", background: "var(--layer2)", color: "var(--muted)",
            }}>{diagLoading ? "…" : "Refresh"}</button>
          </div>

          {!diag && !diagLoading && (
            <div style={{ fontSize: 12, color: "var(--dim)", textAlign: "center", padding: "20px 0" }}>Click Refresh to load stats</div>
          )}
          {diagLoading && (
            <div style={{ fontSize: 12, color: "var(--dim)", textAlign: "center", padding: "20px 0" }}>Loading…</div>
          )}

          {diag?._error && (
            <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)", fontSize: 12, color: "#f87171" }}>{diag._error}</div>
          )}

          {diag && !diag._error && (() => {
            const lat = diag.latency_ms || {};
            const brk = diag.breaker || {};
            const breakerOpen = brk.open;
            const successRate = diag.total_calls > 0
              ? Math.round(diag.total_success / diag.total_calls * 100)
              : null;
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Breaker state + reset button */}
                <div style={{
                  padding: "10px 14px", borderRadius: 10,
                  background: breakerOpen ? "rgba(248,113,113,0.08)" : "rgba(74,222,128,0.08)",
                  border: `1px solid ${breakerOpen ? "rgba(248,113,113,0.35)" : "rgba(74,222,128,0.35)"}`,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: breakerOpen ? "#f87171" : "#4ade80", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: breakerOpen ? "#f87171" : "#4ade80" }}>
                      Circuit Breaker {breakerOpen ? "OPEN — LLM paused" : "Closed — healthy"}
                    </div>
                    {breakerOpen && brk.opened_at && (
                      <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 2 }}>
                        Opened {new Date(brk.opened_at).toLocaleTimeString()} · auto-resets after {brk.open_duration_sec}s
                      </div>
                    )}
                    {!breakerOpen && (
                      <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 2 }}>
                        Trips after {brk.fail_threshold} consecutive failures · {brk.consecutive_failures ?? 0} current
                      </div>
                    )}
                  </div>
                  {breakerOpen && (
                    <button onClick={resetBreaker} disabled={breakerResetting} style={{
                      padding: "5px 10px", borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: breakerResetting ? "default" : "pointer",
                      border: "1px solid rgba(248,113,113,0.4)", background: "rgba(248,113,113,0.12)", color: "#f87171",
                    }}>{breakerResetting ? "…" : "Force Reset"}</button>
                  )}
                </div>

                {/* Latency */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    { label: "P50 latency", val: lat.p50 != null ? `${(lat.p50 / 1000).toFixed(1)}s` : "—", warn: lat.p50 > 10000 },
                    { label: "P95 latency", val: lat.p95 != null ? `${(lat.p95 / 1000).toFixed(1)}s` : "—", warn: lat.p95 > 20000 },
                    { label: "P99 latency", val: lat.p99 != null ? `${(lat.p99 / 1000).toFixed(1)}s` : "—", warn: lat.p99 > 25000 },
                    { label: "Max latency",  val: lat.max != null ? `${(lat.max / 1000).toFixed(1)}s` : "—", warn: lat.max > 30000 },
                  ].map(({ label, val, warn }) => (
                    <div key={label} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                      <div style={{ fontSize: 10, color: "var(--dim)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: warn ? "#fbbf24" : "var(--text)", fontVariantNumeric: "tabular-nums" }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Call counts + success rate */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                  {[
                    { label: "Total calls",  val: diag.total_calls ?? "—",    color: "#a78bfa" },
                    { label: "Success rate", val: successRate != null ? `${successRate}%` : "—", color: successRate != null && successRate >= 90 ? "#4ade80" : "#fbbf24" },
                    { label: "Errors",        val: diag.total_errors ?? "—",   color: "#f87171" },
                    { label: "Timeouts",      val: diag.total_timeouts ?? "—", color: "#fbbf24" },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--layer2)", border: "1px solid var(--border)", textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Cost — total spend + per-model breakdown, real usage-based numbers from OpenRouter, never estimated */}
                <div style={{ padding: "10px 12px", borderRadius: 8, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: diag.per_model && Object.keys(diag.per_model).length ? 8 : 0 }}>
                    <div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.4 }}>Total LLM spend</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#4ade80" }}>${(diag.total_cost_usd ?? 0).toFixed(4)}</div>
                  </div>
                  {diag.per_model && Object.keys(diag.per_model).length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {Object.entries(diag.per_model)
                        .sort((a, b) => (b[1]?.cost_usd || 0) - (a[1]?.cost_usd || 0))
                        .map(([modelId, m]) => (
                          <div key={modelId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 10 }}>
                            <span style={{ color: "var(--text)", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>{modelId}</span>
                            <span style={{ color: "var(--dim)" }}>{m.calls} call{m.calls === 1 ? "" : "s"} · <span style={{ color: "#4ade80" }}>${(m.cost_usd || 0).toFixed(4)}</span></span>
                          </div>
                        ))}
                    </div>
                  )}
                  {diag.last_model_used && (
                    <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 6 }}>
                      Last model used: <span style={{ color: "var(--text)", fontFamily: "monospace" }}>{diag.last_model_used}</span>
                    </div>
                  )}
                </div>

                {/* Last call + last error */}
                {(diag.last_call_at || diag.last_error_msg) && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {diag.last_call_at && (
                      <div style={{ fontSize: 10, color: "var(--dim)" }}>
                        Last call: <span style={{ color: "var(--text)" }}>{new Date(diag.last_call_at).toLocaleTimeString()}</span>
                      </div>
                    )}
                    {diag.last_error_msg && (
                      <div style={{ padding: "6px 10px", borderRadius: 6, background: "rgba(248,113,113,0.07)", border: "1px solid rgba(248,113,113,0.2)", fontSize: 10, color: "#f87171", fontFamily: "monospace", wordBreak: "break-all" }}>
                        Last error: {diag.last_error_msg}
                      </div>
                    )}
                  </div>
                )}

                {/* Config */}
                <div style={{ padding: "8px 12px", borderRadius: 8, background: "var(--layer2)", border: "1px solid var(--border)", fontSize: 10, color: "var(--dim)", lineHeight: 1.6 }}>
                  Model: <span style={{ color: "var(--text)" }}>{diag.config?.default_model || "—"}</span>
                  {" · "}Timeout: <span style={{ color: "var(--text)" }}>{diag.config?.effective_timeout_sec ?? diag.config?.default_timeout_sec}s</span>
                  {diag.config?.effective_timeout_sec !== diag.config?.default_timeout_sec && (
                    <span style={{ color: "#fbbf24" }}> (adaptive)</span>
                  )}
                  {" · "}Key: <span style={{ color: diag.config?.key_configured ? "#4ade80" : "#f87171" }}>{diag.config?.key_configured ? "✓ set" : "✗ missing"}</span>
                  {" · "}Samples: <span style={{ color: "var(--text)" }}>{lat.samples ?? 0}</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── FLOOR CHAT ──────────────────────────────────────────────────── */}
      {section === "floor" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.5 }}>Floor Wire History</div>
            <div style={{ fontSize: 12, color: "var(--dim)", marginBottom: 12, lineHeight: 1.6 }}>
              The trading floor chat persists across navigation and tab switches. Use the button below to clear all stored messages and start a fresh session.
            </div>
            <button
              onClick={() => {
                try { localStorage.removeItem("roi_floor_chat_v1"); } catch {}
                window.dispatchEvent(new CustomEvent("floor_chat_cleared"));
                alert("Floor chat history cleared. Reload the Floor tab to see a fresh wire.");
              }}
              style={{ padding: "10px 18px", borderRadius: 10, background: "rgba(251,113,133,0.15)", border: "1px solid rgba(251,113,133,0.38)", color: "#fb7185", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
            >
              Clear Floor Chat History
            </button>
          </div>
        </div>
      )}

      <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
        All settings are saved instantly. Changes apply to the next message you send to {settings.assistant_name || "Omega"}.
      </div>
    </div>
  );
}
